<template>
    <div class="payment bg-cover bg-center bg-no-repeat px-3 pt-40 pb-20 z-10">
        <div
            class="realitve flex flex-col items-center max-w-screen-sm mx-auto border-2 border-orange mb-6 py-16 bg-white"
        >
            <SpriteAnim
                v-if="!$isMobile()"
                :json="
                    require('@/assets/animations/panchito_top_left-min.json')
                "
                :png="require('@/assets/animations/panchito_top_left-min.png')"
                :fps="24"
                class="paco hidden lg:block absolute w-48 sm:w-64"
            />

            <div class="flex flex-col items-center mb-8">
                <brush-stroke-bg class="mb-6">
                    <h2
                        class="font-primary text-3xl text-white font-semibold py-2 pr-6"
                    >
                        {{ $config("M_PAGO") }}
                    </h2>
                </brush-stroke-bg>

                <p class="font-secondary text-blue font-semibold text-center">
                    {{ $config("P_TEST") }}
                </p>
            </div>

            <div
                class="flex flex-col w-10/12 sm:w-2/3 font-secondary text-gray-600 font-semibold"
            >
                <p class="mb-4">{{ $config("PAY_TODAY") }}</p>

                <ul class="list-disc mb-10">
                    <li>{{ $config("CONFIGURE_PAYMENT_FOR_LATER") }}</li>
                    <li>{{ $config("NO_CHARGE_UNTIL") }} {{ trialEndDate }}</li>
                    <li>
                        {{ $config("AMOUNT_AFTER_TRIAL") }}
                        {{ selectedPlanPrice }}
                    </li>
                </ul>

                <div
                    class="flex flex-col pointer"
                    role="button"
                    @click="toggleCardDataSection"
                >
                    <router-link
                        class="font-secondary text-sm text-right mr-2 mb-1 text-blue self-end"
                        :to="{ name: 'register-select-plan' }"
                        >{{ $config("CHANGE_PLAN") }}</router-link
                    >

                    <div
                        class="flex items-center h-18 border-2 border-blue-lightest py-1 mb-6"
                    >
                        <app-image
                            url="Cards.svg"
                            is-local
                            :try-webp="false"
                            class="cards-img w-10/12 flex-grow"
                        ></app-image>

                        <font-awesome-icon
                            :icon="
                                showCardDataSection
                                    ? 'chevron-down'
                                    : 'chevron-right'
                            "
                            class="ml-2 mr-4 font-bold text-4xl text-blue-lightest"
                        />
                    </div>
                </div>

                <ValidationObserver v-slot="{ handleSubmit }" class="w-full">
                    <form
                        class="flex flex-col"
                        @submit.prevent="handleSubmit(dispatchPayment)"
                        novalidate
                    >
                        <slide-up-down
                            :active="showCardDataSection"
                            :duration="600"
                        >
                            <div class="flex flex-col">
                                <form-input
                                    name="name_on_card"
                                    v-model="nameOnCard"
                                    type="text"
                                    :placeholder="$config('NAME')"
                                    class="mb-3"
                                    required
                                ></form-input>

                                <form-input
                                    name="card_number"
                                    v-model="cardNumber"
                                    type="tel"
                                    :placeholder="$config('TARJETA')"
                                    class="mb-3"
                                    required
                                ></form-input>

                                <div
                                    class="flex items-center justify-between flex-wrap mb-6"
                                >
                                    <div class="w-1/2 px-1 sm:px-4 mb-4">
                                        <ValidationProvider
                                            name="card_expiry_month"
                                            rules="required"
                                            mode="lazy"
                                            v-slot="{ errors }"
                                            class="flex flex-col"
                                        >
                                            <div
                                                class="flex items-center relative h-10 border-2 border-blue-lightest"
                                            >
                                                <select
                                                    v-model="cardExpiryMonth"
                                                    class="w-full h-full bg-transparent flex-grow appearance-none pl-2"
                                                    required
                                                >
                                                    <option value="">{{
                                                        $config("MES")
                                                    }}</option>

                                                    <option
                                                        v-for="(month,
                                                        index) in creditCardExpiryMonthRange"
                                                        :key="index"
                                                        :value="month"
                                                        >{{ month }}</option
                                                    >
                                                </select>

                                                <font-awesome-icon
                                                    icon="chevron-down"
                                                    class="absolute right-0 pointer-events-none mr-1 text-blue-lightest"
                                                />
                                            </div>

                                            <span
                                                class="font-secondary text-xs text-red-500"
                                                :style="{ minHeight: '20px' }"
                                                >{{ errors[0] }}</span
                                            >
                                        </ValidationProvider>
                                    </div>

                                    <div class="w-1/2 px-1 sm:px-4 mb-4">
                                        <ValidationProvider
                                            name="card_expiry_year"
                                            rules="required"
                                            mode="lazy"
                                            v-slot="{ errors }"
                                            class="flex flex-col"
                                        >
                                            <div
                                                class="flex items-center flex-wrap relative h-10 border-2 border-blue-lightest"
                                            >
                                                <select
                                                    v-model="cardExpiryYear"
                                                    class="w-full h-full bg-transparent flex-grow appearance-none pl-2"
                                                    required
                                                >
                                                    <option value="" selected>{{
                                                        $config("ANO")
                                                    }}</option>
                                                    <option
                                                        :value="year"
                                                        v-for="(year,
                                                        index) in creditCardExpiryYearRange"
                                                        :key="index"
                                                        >{{ year }}</option
                                                    >
                                                </select>

                                                <font-awesome-icon
                                                    icon="chevron-down"
                                                    class="absolute right-0 pointer-events-none mr-1 text-blue-lightest"
                                                />
                                            </div>

                                            <span
                                                class="font-secondary text-xs text-red-500"
                                                :style="{ minHeight: '20px' }"
                                                >{{ errors[0] }}</span
                                            >
                                        </ValidationProvider>
                                    </div>

                                    <div class="w-1/2 px-1 sm:px-4">
                                        <form-input
                                            name="cvv"
                                            v-model="cardCVV"
                                            type="tel"
                                            input-class="pl-2 h-10 border-2"
                                            placeholder="CVV"
                                            required
                                            :input-style="{
                                                paddingBottom: '0px'
                                            }"
                                        ></form-input>
                                    </div>

                                    <div class="w-1/2 px-1 sm:px-4">
                                        <form-input
                                            name="zip_code"
                                            v-model="zipCode"
                                            type="tel"
                                            input-class="w-full pl-2 h-10 border-2"
                                            :placeholder="$config('ZIPCODE')"
                                            :input-style="{
                                                paddingBottom: '0px'
                                            }"
                                        ></form-input>
                                    </div>
                                </div>
                            </div>
                        </slide-up-down>

                        <button
                            type="submit"
                            class="hidden"
                            ref="submitPaymentFormButton"
                        ></button>
                    </form>
                </ValidationObserver>

                <div
                    class="flex items-center h-18 border-2 border-blue-lightest py-1 mb-6 pointer"
                    role="button"
                    @click="togglePaypalSection"
                >
                    <app-image
                        url="paypal.png"
                        is-local
                        :try-webp="false"
                        class="cards-img w-10/12 flex-grow"
                    ></app-image>

                    <font-awesome-icon
                        :icon="
                            showPaypalSection ? 'chevron-down' : 'chevron-right'
                        "
                        class="ml-2 mr-4 font-bold text-4xl text-blue-lightest"
                    />
                </div>

                <slide-up-down :active="showPaypalSection" :duration="600">
                    <div>
                        <p class="font-secondary text-xl text-blue text-center">
                            {{ $config("COMING_SOON") }}
                        </p>
                    </div>
                </slide-up-down>

                <promocode
                    @validated="
                        ({ validatedPromocode }) =>
                            (this.promocode = validatedPromocode.braintree_code)
                    "
                    @removed="() => (this.promocode = null)"
                    class="my-8"
                >
                </promocode>

                <p
                    v-html="$config('ACCEPT_TERMS')"
                    class="font-secondary text-gray-500 text-sm font-semibold tracking-wider mt-5 ml-5 sm:ml-10 mb-8"
                ></p>

                <div class="flex flex-col items-center sm:w-2/3 mx-auto">
                    <btn
                        class="bg-yellow hover:bg-yellow-dark text-xl text-white px-10 py-4 mb-6"
                        @click="submitPaymentForm"
                        >{{ $config("START_T") }}</btn
                    >

                    <p
                        v-html="$config('NO_CHARGE')"
                        class="font-secondary text-gray-500 text-sm font-semibold tracking-wider text-center"
                    ></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import allowLimitedAccessOnly from "../../mixins/allowLimitedAccessOnly";
import requestWithInteractions from "../../mixins/requestWithInteractions";
import FormInput from "../../components/FormInput";
import BrushStrokeBg from "../../components/BrushStrokeBg";
import Btn from "../../components/Btn";
import { format, addWeeks, getYear, addYears } from "date-fns";
import api from "../../api";
import SpriteAnim from "../../components/SpriteAnim";
import Promocode from "../../components/Promocode";

export default {
    name: "register-payment",

    components: { FormInput, BrushStrokeBg, Btn, SpriteAnim, Promocode },

    mixins: [allowLimitedAccessOnly, requestWithInteractions],

    data() {
        return {
            showCardDataSection: true,
            showPaypalSection: false,
            nameOnCard: null,
            cardNumber: null,
            cardExpiryYear: "",
            cardExpiryMonth: "",
            cardCVV: null,
            zipCode: null,
            promocode: null
        };
    },

    computed: {
        ...mapGetters(["selectedPlanPrice", "selectedPlanSubscriptionCode"]),

        trialEndDate() {
            return format(addWeeks(new Date(), 1), "dd/MM/yyyy");
        },

        creditCardExpiryMonthRange() {
            return this.range(1, 12, 1);
        },

        creditCardExpiryYearRange() {
            let today = new Date();
            let minYear = getYear(today);
            let maxYear = getYear(addYears(today, 7));

            return this.range(minYear, maxYear, 1);
        }
    },

    methods: {
        submitPaymentForm() {
            this.showCardDataSection = true;
            setTimeout(() => this.$refs.submitPaymentFormButton.click(), 100);
        },

        dispatchPayment() {
            let paymentRequest = api.payment(
                this.selectedPlanSubscriptionCode,
                this.nameOnCard,
                this.cardNumber,
                this.cardExpiryMonth,
                this.cardExpiryYear,
                this.cardCVV,
                this.zipCode,
                this.promocode
            );

            this.sendRequest(paymentRequest, {
                beforeSendText: this.$config("T_SENDING"),
                successText: this.$config("T_CONFIRM"),
                onSuccess: response => {
                    this.$store.commit(
                        "SET_AUTHENTICATED_USER",
                        response.userdata
                    );
                    this.$store.commit(
                        "SET_AUTHENTICATED_USER_ACCESS",
                        response.access
                    );

                    this.$router.push({ name: "home" });
                }
            });
        },

        toggleCardDataSection() {
            this.showCardDataSection = !this.showCardDataSection;
        },

        togglePaypalSection() {
            this.showPaypalSection = !this.showPaypalSection;
        },

        range(start, stop, step) {
            return Array.from(
                { length: (stop - start) / step + 1 },
                (_, i) => start + i * step
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.payment {
    background-image: url("~@/assets/img/fondos_pinguiigluu.jpg");

    .paco {
        right: 25px;
        top: 50%;
    }

    .cards-img {
        margin-left: -20px;

        @media (max-width: 500px) {
            margin-left: -10px;
        }
    }
}
</style>
